"use client";

import React, { useEffect, useMemo, useState } from "react";

const TICK_MS = 45;
const DEFAULT_START_DELAY_MS = 520;
const MIN_DURATION_MS = 1800;
const MAX_DURATION_MS = 6000;
const MS_PER_CHARACTER = 72;

type WriteInTag = "p" | "span" | "div";

interface WhiteboardWriteInTextProps extends React.HTMLAttributes<HTMLElement> {
  text: string;
  as?: WriteInTag;
  inline?: boolean;
  initialDelayMs?: number;
  streamKey?: string;
  onComplete?: () => void;
}

function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(query.matches);

    const handleChange = () => setPrefersReducedMotion(query.matches);
    query.addEventListener?.("change", handleChange);
    return () => query.removeEventListener?.("change", handleChange);
  }, []);

  return prefersReducedMotion;
}

export const WhiteboardWriteInText: React.FC<WhiteboardWriteInTextProps> = ({
  text,
  as = "span",
  inline = false,
  initialDelayMs = 0,
  streamKey,
  onComplete,
  className,
  ...rest
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const chars = useMemo(() => Array.from(text), [text]);
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    if (chars.length === 0) {
      setVisibleCount(chars.length);
      onComplete?.();
      return;
    }

    if (prefersReducedMotion) {
      setVisibleCount(chars.length);
      onComplete?.();
      return;
    }

    setVisibleCount(0);
    const duration = Math.min(
      MAX_DURATION_MS,
      Math.max(MIN_DURATION_MS, chars.length * MS_PER_CHARACTER)
    );
    let elapsed = 0;
    let interval: ReturnType<typeof setInterval> | null = null;

    const timeout = setTimeout(
      () => {
        interval = setInterval(() => {
          elapsed = Math.min(duration, elapsed + TICK_MS);
          const progress = elapsed / duration;
          const nextCount =
            progress >= 1 ? chars.length : Math.max(1, Math.floor(chars.length * progress));
          setVisibleCount(nextCount);
          if (nextCount >= chars.length) {
            if (interval) clearInterval(interval);
            onComplete?.();
          }
        }, TICK_MS);
      },
      DEFAULT_START_DELAY_MS + Math.max(0, initialDelayMs)
    );

    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [chars, chars.length, initialDelayMs, onComplete, prefersReducedMotion, streamKey]);

  const Tag = as;
  const visibleText = chars.slice(0, visibleCount).join("");
  const complete = visibleCount >= chars.length;

  return (
    <Tag
      {...rest}
      aria-label={rest["aria-label"] ?? text}
      className={`whiteboard-write-in-text ${inline ? "whiteboard-write-in-text--inline" : ""} ${
        className ?? ""
      }`}
      data-whiteboard-write-complete={complete ? "true" : "false"}
      data-whiteboard-reduced-motion={prefersReducedMotion ? "true" : "false"}
    >
      <span aria-hidden="true" data-whiteboard-write-visible>
        {visibleText}
      </span>
    </Tag>
  );
};
